/**
 * In-memory access token store.
 *
 * Security stance: access tokens NEVER hit localStorage or sessionStorage
 * (they're XSS-exfiltrable there). Refresh tokens are httpOnly cookies
 * set by the server — invisible to JS by design. On page reload, the
 * auth provider fires a silent /v1/auth/refresh; if the cookie is still
 * valid, a new access token materializes in memory without the user
 * clicking anything.
 *
 * This module is the single source of truth. Every fetch goes through
 * it. Tests reset it between runs.
 */

let accessToken: string | null = null;

/** Subscribers notified when the token changes (login/logout/refresh). */
type Subscriber = (token: string | null) => void;
const subscribers = new Set<Subscriber>();

export function getAccessToken(): string | null {
  return accessToken;
}

export function setAccessToken(token: string): void {
  accessToken = token;
  for (const fn of subscribers) fn(token);
}

export function clearAccessToken(): void {
  accessToken = null;
  for (const fn of subscribers) fn(null);
}

/**
 * Subscribe to token changes. Returns an unsubscribe function.
 * The AuthContext uses this to keep React state in sync with the
 * imperative store (so apiRequest can read the token without React).
 */
export function subscribeToAccessToken(fn: Subscriber): () => void {
  subscribers.add(fn);
  return () => {
    subscribers.delete(fn);
  };
}

/** Test-only: reset state between tests. */
export function __resetTokenStoreForTests(): void {
  accessToken = null;
  subscribers.clear();
}
